import { ReactNode, useState } from 'react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import useGetBookingCodePrefix from '@/features/dashboard/bookingCodePrefix/hooks/useGetBookingCodePrefix.ts';
import EditBookingCodePrefixModal from '@/features/dashboard/bookingCodePrefix/components/EditBookingCodePrefixModal.tsx';
import useGetConsentCodePrefix from '@/features/dashboard/consentCodePrefix/hooks/useGetConsentCodePrefix.ts';
import EditConsentCodePrefixModal from '@/features/dashboard/consentCodePrefix/components/EditConsentCodePrefixModal.tsx';

interface PrefixRowProps {
  label: string;
  prefix: string | null;
  exampleNumber: string;
  onEdit: () => void;
}

const PrefixRow = ({
  label,
  prefix,
  exampleNumber,
  onEdit,
}: PrefixRowProps) => {
  /*
   * Brak prefiksu to wciąż jedna wartość w wierszu, nie pusty ekran — pełny
   * `NoItems` z kafelkiem ikony rozpychał tu dwuzdaniowy blok pod jedną liczbę.
   */
  const value: ReactNode = (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {prefix ? (
        <span className="font-mono text-sm tracking-[0.04em] text-base-text">
          {prefix}
        </span>
      ) : (
        <span className="text-[13.5px] font-normal text-neutral-600">
          Brak prefiksu
        </span>
      )}
      <span className="text-[12.5px] font-normal text-neutral-600">
        przykład: {prefix ? `${prefix}-${exampleNumber}` : exampleNumber}
      </span>
    </div>
  );

  return (
    <InfoCardRow
      layout="columns"
      label={label}
      action={
        <ActionButton tone="outline" size="toolbar" onClick={onEdit}>
          {prefix ? 'Zmień' : 'Dodaj'}
        </ActionButton>
      }
    >
      {value}
    </InfoCardRow>
  );
};

/**
 * Numery rezerwacji i kody zgód mają osobne zapytania i osobne modale, ale ta
 * sama reguła rządzi obydwoma — rozbicie na dwie sekcje kazało czytać ją dwa
 * razy.
 */
const PrefixesSection = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const { data: bookingData, isLoading: isBookingLoading } =
    useGetBookingCodePrefix();
  const { data: consentData, isLoading: isConsentLoading } =
    useGetConsentCodePrefix();

  if (isBookingLoading || isConsentLoading) {
    return <SectionLayoutSkeleton rows={2} />;
  }

  const bookingPrefix = bookingData?.prefix ?? null;
  const consentPrefix = consentData?.prefix ?? null;

  return (
    <>
      <SectionLayout
        title="Prefiksy"
        description="Prefiks stoi na początku każdego nowego numeru. Maksymalnie 6 znaków, wielkie litery i cyfry. Zmiana nie wpływa na już wystawione numery."
      >
        <InfoCardList>
          <PrefixRow
            label="Rezerwacje"
            prefix={bookingPrefix}
            exampleNumber="260823-7MHR8Z"
            onEdit={() => setIsBookingModalOpen(true)}
          />
          <PrefixRow
            label="Kody zgód"
            prefix={consentPrefix}
            exampleNumber="260823-K4P9TW"
            onEdit={() => setIsConsentModalOpen(true)}
          />
        </InfoCardList>
      </SectionLayout>
      {isBookingModalOpen && (
        <EditBookingCodePrefixModal
          currentPrefix={bookingPrefix}
          onClose={() => setIsBookingModalOpen(false)}
        />
      )}
      {isConsentModalOpen && (
        <EditConsentCodePrefixModal
          currentPrefix={consentPrefix}
          onClose={() => setIsConsentModalOpen(false)}
        />
      )}
    </>
  );
};

export default PrefixesSection;
